// Completed jobs view — date-sorted descending. Same RLS path as
// PortalJobs (queries portal_jobs view), but filtered to status=complete
// and rendered with the same card richness so clients have the full
// service-visit story inline (no Tier-2 detail page needed).
//
// View contract note: portal_jobs has NO actual_end / completed_at
// column. The closest signal for "when was this completed" is
// scheduled_date — which for a completed job equals the visit date.
// "Completed on DATE" therefore reads scheduled_date; if a future
// completion-stamp field is added to the view, swap here.

import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { formatDate } from '@/lib/utils'
import { History, Calendar, MapPin, User as UserIcon, Phone, CheckCircle2, ChevronRight } from 'lucide-react'
import { PortalPhotoStrip } from '@/pages/portal/PortalPhotoStrip'
import { portalListVariants, portalCardVariants, portalCardHover } from '@/pages/portal/portalMotion'
import { PortalSkeletonList } from '@/pages/portal/PortalSkeleton'

interface HistoryJob {
  id: string
  job_number: string | null
  title: string | null
  status: string
  scheduled_date: string | null
  scheduled_start: string | null
  scheduled_end: string | null
  address_line1: string | null
  address_line2: string | null
  address_city: string | null
  address_state: string | null
  address_zip: string | null
  job_notes: string | null
  assigned_agent_name: string | null
  csr_name: string | null
}

export default function PortalHistory() {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['portal-jobs-history'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('portal_jobs')
        .select('id, job_number, title, status, scheduled_date, scheduled_start, scheduled_end, address_line1, address_line2, address_city, address_state, address_zip, job_notes, assigned_agent_name, csr_name')
        .eq('status', 'complete')
        .order('scheduled_date', { ascending: false, nullsFirst: false })
      if (error) throw error
      return (data ?? []) as HistoryJob[]
    },
  })

  if (isLoading) {
    return <PortalSkeletonList count={4} height={120} />
  }
  if (rows.length === 0) {
    return (
      <div style={{ padding: '32px 16px', textAlign: 'center' }}>
        <History style={{ width: 28, height: 28, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>No completed jobs yet</p>
        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>Your service history will build up here as visits wrap up.</p>
      </div>
    )
  }

  return (
    <motion.div initial="hidden" animate="visible" variants={portalListVariants}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rows.map(job => <HistoryJobCard key={job.id} job={job} />)}
    </motion.div>
  )
}

function HistoryJobCard({ job }: { job: HistoryJob }) {
  const navigate = useNavigate()
  const address = useMemo(() => composeAddress(job), [job])
  const schedule = useMemo(() => composeSchedule(job), [job])

  const onCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('a')) return
    navigate(`/portal/jobs/${job.id}`)
  }

  return (
    <motion.article variants={portalCardVariants} whileHover={portalCardHover}
      role="link" tabIndex={0}
      onClick={onCardClick}
      onKeyDown={e => { if (e.key === 'Enter') navigate(`/portal/jobs/${job.id}`) }}
      style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderLeft: '3px solid var(--positive)', borderRadius: 12, padding: '14px 16px', cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', fontFamily: 'monospace' }}>{job.job_number ?? ''}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: 'var(--positive-dim)', color: 'var(--positive)', border: '1px solid var(--positive-border)' }}>
          <CheckCircle2 style={{ width: 10, height: 10 }} />
          Completed
        </span>
        {job.scheduled_date && (
          <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>
            {formatDate(job.scheduled_date, 'MMM d, yyyy')}
          </span>
        )}
        <ChevronRight style={{ width: 12, height: 12, color: 'var(--ink-tertiary)', marginLeft: 'auto' }} />
      </div>
      <h3 style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 8px', letterSpacing: '-0.01em' }}>{job.title ?? 'Untitled'}</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 10 }}>
        {schedule && <Meta icon={<Calendar style={iconStyle} />} text={schedule} />}
        {address && <Meta icon={<MapPin style={iconStyle} />} text={address} />}
        {job.assigned_agent_name && <Meta icon={<UserIcon style={iconStyle} />} text={`Tech: ${job.assigned_agent_name}`} />}
        {job.csr_name && <Meta icon={<Phone style={iconStyle} />} text={`Coordinator: ${job.csr_name}`} />}
      </div>
      {job.job_notes && job.job_notes.trim() && (
        <div style={{ marginBottom: 10 }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px' }}>Details</p>
          <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{job.job_notes}</p>
        </div>
      )}
      <PortalPhotoStrip jobId={job.id} />
    </motion.article>
  )
}

function Meta({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ink-secondary)' }}>
      {icon}
      <span>{text}</span>
    </div>
  )
}

const iconStyle: React.CSSProperties = { width: 12, height: 12, color: 'var(--ink-tertiary)', flexShrink: 0 }

// ─── helpers ────────────────────────────────────────────────────────
function composeAddress(j: HistoryJob): string | null {
  const street = [j.address_line1, j.address_line2].filter(Boolean).join(', ')
  const region = [j.address_city, j.address_state].filter(Boolean).join(', ')
  const full = [street, region, j.address_zip].filter(Boolean).join(' · ')
  return full || null
}
function composeSchedule(j: HistoryJob): string | null {
  if (!j.scheduled_date && !j.scheduled_start) return null
  const parts: string[] = []
  if (j.scheduled_date) parts.push(formatDate(j.scheduled_date, 'EEE, MMM d'))
  if (j.scheduled_start && j.scheduled_end) parts.push(`${j.scheduled_start}–${j.scheduled_end}`)
  else if (j.scheduled_start) parts.push(j.scheduled_start)
  return parts.join(' · ')
}
